// The site's chat (shell/chat.html): a visitor asks, the assistant answers from
// what this site says, and the team - who see every conversation - can answer
// in it themselves. The api decides whether the chat is open at all; without a
// yes the page carries nothing. The conversation's credential is the visitor's
// only key to it and lives on this device alone, written the moment they send
// their first question and never before: opening the panel stores nothing.
import './chat.css';

export const CHAT_KEY = 'edtechcore.sitechat';

export type ChatMessage = { id: number; author: 'visitor' | 'bot' | 'operator'; body: string; createdAt: string };
export type Conversation = { mode: 'bot' | 'operator' | 'closed'; waiting: boolean; answerByEmail?: boolean; messages: ChatMessage[] };

/** How long a conversation handed to the operator waits for their answer before
 *  the visitor is offered the answer by email. */
export const SLOW_OPERATOR_MS = 10 * 60 * 1000;

/** Whether to offer the answer by email: the conversation is the operator's, the
 *  last word is the assistant's hand-over, it has waited past SLOW_OPERATOR_MS,
 *  and no address is kept yet. */
export function offerEmail(c: Conversation, now: number): boolean {
  if (c.mode !== 'operator' || c.answerByEmail) return false;
  const last = c.messages[c.messages.length - 1];
  if (!last || last.author === 'operator') return false;
  const handed = [...c.messages].reverse().find((m) => m.author === 'bot');
  if (!handed || c.messages.some((m) => m.author === 'operator' && m.id > handed.id)) return false;
  return now - Date.parse(handed.createdAt) >= SLOW_OPERATOR_MS;
}

/** How long to wait before the next read: soon while an answer is on its way,
 *  calmly while the conversation is quiet - the team may still write in it -
 *  and slowly once it is closed, since the team may open it again. */
export function nextRead(c: Conversation | null): number {
  if (!c) return 0;
  if (c.mode === 'closed') return 20000;
  return c.waiting ? 2500 : 6000;
}

/** The parts of a message as the page writes them: text, and a link only for
 *  an address on this site - an answer can name a page, never send a visitor
 *  elsewhere. */
export function linkParts(body: string, origin: string): Array<{ text: string; href?: string }> {
  const out: Array<{ text: string; href?: string }> = [];
  let last = 0;
  for (const m of body.matchAll(/https?:\/\/[^\s<>"')]+[^\s<>"').,;:!?]/g)) {
    let href: URL | null = null;
    try {
      href = new URL(m[0]);
    } catch {
      href = null;
    }
    if (!href || href.origin !== origin) continue;
    const at = m.index ?? 0;
    if (at > last) out.push({ text: body.slice(last, at) });
    out.push({ text: m[0], href: href.href });
    last = at + m[0].length;
  }
  if (last < body.length) out.push({ text: body.slice(last) });
  return out;
}

function stored(): string | null {
  try {
    return localStorage.getItem(CHAT_KEY);
  } catch {
    return null;
  }
}

function store(credential: string | null): void {
  try {
    if (credential) localStorage.setItem(CHAT_KEY, credential);
    else localStorage.removeItem(CHAT_KEY);
  } catch {
    // A browser that keeps nothing still holds the conversation for this page.
  }
}

type Failure = 'rate' | 'gone' | 'full' | 'closed' | 'off' | 'send';

/** The failures that end what the visitor can do in this conversation. */
const ENDS: readonly Failure[] = ['gone', 'full', 'closed', 'off'];

async function failureOf(res: Response): Promise<Failure> {
  if (res.status === 429) return 'rate';
  let code = '';
  try {
    code = ((await res.json()) as { error?: { code?: string } }).error?.code ?? '';
  } catch {
    code = '';
  }
  if (code === 'sitechat.off') return 'off';
  if (code === 'sitechat.full') return 'full';
  if (code === 'sitechat.closed') return 'closed';
  if (res.status === 404) return 'gone';
  return 'send';
}

export function startChat(): void {
  const root = document.querySelector<HTMLElement>('#sitechat');
  const launcher = document.querySelector<HTMLButtonElement>('#sitechat-open');
  const panel = document.querySelector<HTMLElement>('#sitechat-panel');
  const close = document.querySelector<HTMLButtonElement>('#sitechat-close');
  const body = document.querySelector<HTMLElement>('#sitechat-body');
  const empty = document.querySelector<HTMLElement>('#sitechat-empty');
  const log = document.querySelector<HTMLOListElement>('#sitechat-log');
  const typing = document.querySelector<HTMLElement>('#sitechat-typing');
  const error = document.querySelector<HTMLElement>('#sitechat-error');
  const form = document.querySelector<HTMLFormElement>('#sitechat-form');
  const input = document.querySelector<HTMLTextAreaElement>('#sitechat-input');
  const send = document.querySelector<HTMLButtonElement>('#sitechat-send');
  const restart = document.querySelector<HTMLButtonElement>('#sitechat-restart');
  const slow = document.querySelector<HTMLElement>('#sitechat-slow');
  const emailForm = document.querySelector<HTMLFormElement>('#sitechat-email-form');
  const emailInput = document.querySelector<HTMLInputElement>('#sitechat-email');
  const emailConsent = document.querySelector<HTMLInputElement>('#sitechat-email-consent');
  const emailError = document.querySelector<HTMLElement>('#sitechat-email-error');
  const emailKept = document.querySelector<HTMLElement>('#sitechat-email-kept');
  const del = document.querySelector<HTMLButtonElement>('#sitechat-delete');
  const delAsk = document.querySelector<HTMLElement>('#sitechat-delete-ask');
  const delYes = document.querySelector<HTMLButtonElement>('#sitechat-delete-yes');
  const delNo = document.querySelector<HTMLButtonElement>('#sitechat-delete-no');
  const deleted = document.querySelector<HTMLElement>('#sitechat-deleted');
  if (!root || !launcher || !panel || !close || !body || !empty || !log || !typing || !error || !form || !input || !send || !restart) return;
  const line = (id: string) => document.querySelector<HTMLElement>(`#sitechat-${id}`)?.textContent?.trim() ?? '';

  let credential = stored();
  let conversation: Conversation | null = null;
  let timer = 0;
  let sending = false;
  let shown: Failure | null = null;
  // What the log already holds: a read appends only the messages after these,
  // so a screen reader announces the new line - not the whole conversation every
  // few seconds - and a link a keyboard is on keeps its focus.
  let rendered: number[] = [];
  let taken = false;

  const say = (f: Failure | null) => {
    shown = f;
    error.hidden = f === null;
    error.textContent = f ? line(`err-${f}`) : '';
    const over = f !== null && ENDS.includes(f);
    form.hidden = over;
    restart.hidden = !over || f === 'off';
  };

  const clearLog = () => {
    log.replaceChildren();
    rendered = [];
    taken = false;
  };

  const render = (c: Conversation) => {
    const atEnd = body.scrollHeight - body.scrollTop - body.clientHeight < 40;
    conversation = c;
    empty.hidden = c.messages.length > 0;
    const same = rendered.every((id, i) => c.messages[i]?.id === id);
    if (!same) clearLog();
    for (const m of c.messages.slice(rendered.length)) {
      rendered.push(m.id);
      if (m.author === 'operator' && !taken) {
        taken = true;
        const note = document.createElement('li');
        note.className = 'lp-chat-taken';
        note.textContent = line('taken');
        log.append(note);
      }
      const li = document.createElement('li');
      li.className = `lp-chat-msg lp-chat-msg--${m.author}`;
      const who = document.createElement('span');
      who.className = m.author === 'visitor' ? 'lp-visually-hidden' : 'lp-chat-who';
      who.textContent = line(`who-${m.author}`);
      const text = document.createElement('p');
      for (const part of linkParts(m.body, location.origin)) {
        if (part.href) {
          const a = document.createElement('a');
          a.href = part.href;
          a.textContent = part.text;
          text.append(a);
        } else {
          text.append(part.text);
        }
      }
      li.append(who, text);
      log.append(li);
    }
    typing.hidden = !c.waiting;
    if (slow) slow.hidden = !offerEmail(c, Date.now());
    if (emailKept) emailKept.hidden = !c.answerByEmail;
    if (del) del.hidden = !credential || (delAsk ? !delAsk.hidden : false);
    if (c.mode === 'closed') say('closed');
    // Opened again by the team: the visitor may write again.
    else if (shown === 'closed') say(null);
    if (atEnd || sending) body.scrollTop = body.scrollHeight;
  };

  const schedule = () => {
    window.clearTimeout(timer);
    // A conversation not read yet - the first read failed - is tried again.
    const wait = conversation ? nextRead(conversation) : 6000;
    if (panel.hidden || !credential || document.hidden) return;
    timer = window.setTimeout(() => void read(), wait);
  };

  const read = async () => {
    if (!credential) return;
    try {
      const res = await fetch(`/api/v1/site-chat/${encodeURIComponent(credential)}`, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        render((await res.json()) as Conversation);
      } else if ((await failureOf(res)) === 'gone') {
        // A read sent nothing, so only a conversation that is gone is the
        // visitor's to hear about; anything else is tried again.
        credential = null;
        store(null);
        say('gone');
        return;
      }
    } catch {
      // A read that did not reach the api is tried again on the next tick.
    }
    schedule();
  };

  const open = (on: boolean) => {
    panel.hidden = !on;
    launcher.setAttribute('aria-expanded', String(on));
    root.classList.toggle('lp-chat--open', on);
    if (on) {
      input.focus();
      if (credential && !conversation) void read();
      else schedule();
    } else {
      window.clearTimeout(timer);
      launcher.focus();
    }
  };

  // The chat stands on top of a cookie bar while one is up, whatever height the
  // bar's text gives it on this screen; the page keeps room under its end for
  // both (chat.css).
  const bars = [...document.querySelectorAll<HTMLElement>('#cookiebar, #consentbar, #lp-sticky')];
  const lift = () => {
    const bar = bars.find((b) => !b.hidden && b.offsetHeight > 0);
    document.documentElement.style.setProperty('--lp-chat-lift', `${bar ? bar.offsetHeight : 0}px`);
    document.documentElement.classList.toggle('lp-chat-over-bar', !!bar);
  };
  if (bars.length) {
    const watch = new ResizeObserver(lift);
    const shown = new MutationObserver(lift);
    for (const b of bars) {
      watch.observe(b);
      shown.observe(b, { attributes: true, attributeFilter: ['hidden'] });
    }
  }
  lift();

  launcher.addEventListener('click', () => open(panel.hidden));
  close.addEventListener('click', () => open(false));
  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') open(false);
  });
  document.addEventListener('visibilitychange', () => (document.hidden ? window.clearTimeout(timer) : schedule()));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      form.requestSubmit();
    }
  });
  // Back to an empty chat: a new conversation, or none at all after a delete.
  const forgetHere = () => {
    credential = null;
    conversation = null;
    store(null);
    clearLog();
    empty.hidden = false;
    typing.hidden = true;
    if (slow) slow.hidden = true;
    if (emailKept) emailKept.hidden = true;
    if (del) del.hidden = true;
    if (delAsk) delAsk.hidden = true;
    say(null);
  };
  restart.addEventListener('click', () => {
    forgetHere();
    input.focus();
  });

  // The visitor's delete, asked once: gone from the service at once, and from
  // this device. A conversation the service no longer holds is gone either way.
  del?.addEventListener('click', () => {
    if (!delAsk) return;
    del.hidden = true;
    delAsk.hidden = false;
    delNo?.focus();
  });
  delNo?.addEventListener('click', () => {
    if (delAsk) delAsk.hidden = true;
    if (del) {
      del.hidden = !credential;
      del.focus();
    }
  });
  delYes?.addEventListener('click', async () => {
    if (!credential) return;
    try {
      const res = await fetch(`/api/v1/site-chat/${encodeURIComponent(credential)}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 404) throw new Error(String(res.status));
    } catch {
      say('send');
      return;
    }
    forgetHere();
    if (deleted) {
      deleted.hidden = false;
      window.setTimeout(() => (deleted.hidden = true), 6000);
    }
    input.focus();
  });

  // The answer by email: an address, and the visitor's agreement that it serves
  // this answer alone - the server asks for both too.
  emailForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!credential || !emailInput || !emailConsent || !emailError) return;
    const email = emailInput.value.trim();
    const problem = !emailConsent.checked ? 'err-email-consent' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'err-email' : '';
    emailError.hidden = problem === '';
    emailError.textContent = problem ? line(problem) : '';
    if (problem) return;
    try {
      const res = await fetch(`/api/v1/site-chat/${encodeURIComponent(credential)}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent: true }),
      });
      if (res.status === 400) {
        emailError.hidden = false;
        emailError.textContent = line('err-email');
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
    } catch {
      emailError.hidden = false;
      emailError.textContent = line('err-send');
      return;
    }
    emailInput.value = '';
    emailConsent.checked = false;
    if (conversation) render({ ...conversation, answerByEmail: true });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const message = input.value.trim();
    if (!message) {
      say(null);
      error.hidden = false;
      error.textContent = line('err-empty');
      return;
    }
    if (sending) return;
    sending = true;
    send.disabled = true;
    try {
      const res = credential
        ? await fetch(`/api/v1/site-chat/${encodeURIComponent(credential)}/messages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ message }),
          })
        : await fetch('/api/v1/site-chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ message, locale: document.documentElement.lang || 'en', page: location.pathname }),
          });
      if (res.ok) {
        const got = (await res.json()) as Conversation | { credential: string; conversation: Conversation };
        if ('credential' in got) {
          credential = got.credential;
          store(credential);
          // The page's moment for the tag, if the visitor allowed one (main.ts).
          window.dispatchEvent(new CustomEvent('lp:chat-start'));
          render(got.conversation);
        } else {
          render(got);
        }
        input.value = '';
        say(null);
      } else {
        const f = await failureOf(res);
        if (f === 'gone') {
          credential = null;
          store(null);
        }
        say(f);
      }
    } catch {
      say('send');
    } finally {
      sending = false;
      send.disabled = false;
      schedule();
    }
  });

  void (async () => {
    try {
      const res = await fetch('/api/v1/site-chat/status', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const { enabled } = (await res.json()) as { enabled: boolean };
      root.hidden = !enabled;
      document.body.classList.toggle('lp-has-chat', enabled);
    } catch {
      // No api, no chat: the page is whole without it.
    }
  })();
}
